import React, { useState } from 'react';
import theme from '../../theme/globalTheme';
import { Card, Upload, Alert, Typography, List, Tag, Divider, Button } from 'antd';
import { UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, DownloadOutlined, ToolOutlined } from '@ant-design/icons';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';

const { Title, Text, Paragraph } = Typography;

function TemplateValidator() {
  const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [originalZip, setOriginalZip] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);

  // Variables communes à tous les documents
  const commonVariables = [
    'today_date',
    'generation_date',
    'charter_date',
    'from_hour',
    'to_hour',
    'client_name',
    'charterer',
    'brand_of_vessel',
    'name_of_vessel',
    'length',
    'cruising_area',
    'flight_info',
    'pax_number',
    'boat_price',
    'transport_price',
    'transport_text',
    'taxes_price',
    'taxes_text',
    'discount_price',
    'discount_text',
    'extra_hour_price',
    'extra_hour_text',
    'custom_price',
    'custom_text',
    'total_price'
  ];

  // Fonction pour déterminer les variables attendues selon le type de document
  const getExpectedVariables = (fileName) => {
    const lowerFileName = fileName.toLowerCase();

    // Facturation nécessite invoice_number
    if (lowerFileName.includes('facturation') || lowerFileName.includes('invoice')) {
      return [...commonVariables, 'invoice_number'];
    }

    // Contrat et Confirmation n'ont PAS besoin de invoice_number
    return commonVariables;
  };

  const validateTemplate = async (file) => {
    setLoading(true);
    setValidationResult(null);

    try {
      // Lire le fichier Word
      const arrayBuffer = await file.arrayBuffer();
      const zip = new PizZip(arrayBuffer);

      // Sauvegarder le ZIP original pour la correction
      setOriginalZip(zip);
      setOriginalFileName(file.name);

      // Déterminer les variables attendues selon le type de document
      const expectedVariables = getExpectedVariables(file.name);

      // Extraire le XML du document
      const documentXml = zip.file('word/document.xml').asText();

      // Extraire toutes les variables trouvées dans le XML
      const variableRegex = /\{\{(\w+)\}\}/g;
      const foundVariables = [];
      let match;

      while ((match = variableRegex.exec(documentXml)) !== null) {
        const varName = match[1];
        if (!foundVariables.includes(varName)) {
          foundVariables.push(varName);
        }
      }

      // Détecter les variables cassées (avec des balises XML au milieu)
      // Pattern : {{ suivi de texte, puis balises XML, puis plus de texte, puis }}
      const brokenVariableRegex = /\{\{[^}]*?<w:[^>]+>[^}]*?\}\}/g;
      const brokenMatches = documentXml.match(brokenVariableRegex) || [];
      const hasBrokenVariables = brokenMatches.length > 0;

      // Détecter les accolades non fermées
      const unclosedBrackets = (documentXml.match(/\{\{[^}]*?(?!}})/g) || []).length;

      // Comparer avec les variables attendues
      const missingVariables = expectedVariables.filter(v => !foundVariables.includes(v));
      const extraVariables = foundVariables.filter(v => !expectedVariables.includes(v));
      const validVariables = foundVariables.filter(v => expectedVariables.includes(v));

      // Déterminer le statut global
      const isValid = missingVariables.length === 0 &&
                      extraVariables.length === 0 &&
                      !hasBrokenVariables &&
                      unclosedBrackets === 0;

      setValidationResult({
        isValid,
        hasBrokenVariables,
        brokenCount: brokenMatches.length,
        unclosedBrackets,
        foundVariables,
        validVariables,
        missingVariables,
        extraVariables,
        expectedVariables,
        fileName: file.name
      });

    } catch (error) {
      console.error('Erreur validation:', error);
      setValidationResult({
        isValid: false,
        error: `Erreur lors de la validation: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = (file) => {
    if (file.name.endsWith('.docx')) {
      validateTemplate(file);
    } else {
      setValidationResult({
        isValid: false,
        error: 'Veuillez sélectionner un fichier .docx'
      });
    }
    return false; // Empêcher l'upload automatique
  };

  const fixTemplate = async () => {
    if (!originalZip || !validationResult) {
      return;
    }

    setLoading(true);
    try {
      // Extraire le XML du document
      let documentXml = originalZip.file('word/document.xml').asText();

      console.log('🔧 Correction du template en cours...');

      // ÉTAPE 1 : Nettoyer toutes les variables cassées
      // Pattern pour détecter les variables avec des balises XML au milieu
      // Exemple : {{cli<w:rPr><w:b/></w:rPr>ent_name}}
      const brokenVariableRegex = /\{\{([^}]*?(?:<w:[^>]+>)+[^}]*?)\}\}/g;

      documentXml = documentXml.replace(brokenVariableRegex, (match, content) => {
        // Extraire uniquement le texte, supprimer toutes les balises XML
        const cleanText = content.replace(/<[^>]+>/g, '');
        console.log(`  Correction: ${match} → {{${cleanText}}}`);
        return `{{${cleanText}}}`;
      });

      // ÉTAPE 2 : Ajouter les variables manquantes à la fin du document
      if (validationResult.missingVariables && validationResult.missingVariables.length > 0) {
        console.log('📝 Ajout des variables manquantes...');

        // Trouver la balise de fermeture du body
        const bodyCloseTag = '</w:body>';
        const bodyCloseIndex = documentXml.lastIndexOf(bodyCloseTag);

        if (bodyCloseIndex !== -1) {
          // Créer des paragraphes pour chaque variable manquante
          let missingVarsXml = '\n<!-- Variables ajoutées automatiquement par le correcteur -->\n';

          // Grouper les variables par section
          const sections = {
            'Dates': ['today_date', 'generation_date', 'charter_date'],
            'Horaires': ['from_hour', 'to_hour'],
            'Client': ['client_name', 'charterer'],
            'Yacht': ['brand_of_vessel', 'length'],
            'Détails': ['cruising_area', 'flight_info', 'pax_number'],
            'Prix': ['boat_price', 'discount_text', 'discount_amount', 'extra_hour_text', 'extra_hour_price', 'vip_service', 'taxi_service', 'sxm_taxes', 'sbh_taxes', 'axa_taxes', 'total_price'],
            'Informations supplémentaires': ['invoice_number']
          };

          Object.entries(sections).forEach(([sectionName, vars]) => {
            const missingInSection = validationResult.missingVariables.filter(v => vars.includes(v));
            if (missingInSection.length > 0) {
              // Titre de section
              missingVarsXml += `<w:p><w:r><w:t>${sectionName}:</w:t></w:r></w:p>\n`;

              // Variables de la section
              missingInSection.forEach(varName => {
                missingVarsXml += `<w:p><w:r><w:t>{{${varName}}}</w:t></w:r></w:p>\n`;
                console.log(`  Ajouté: {{${varName}}}`);
              });

              // Ligne vide
              missingVarsXml += `<w:p><w:r><w:t></w:t></w:r></w:p>\n`;
            }
          });

          // Insérer avant la fermeture du body
          documentXml = documentXml.slice(0, bodyCloseIndex) + missingVarsXml + documentXml.slice(bodyCloseIndex);
        }
      }

      // ÉTAPE 3 : Remplacer le XML dans le ZIP
      originalZip.file('word/document.xml', documentXml);

      // ÉTAPE 4 : Générer le nouveau fichier
      const blob = originalZip.generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      // ÉTAPE 5 : Télécharger le fichier corrigé
      const fixedFileName = originalFileName.replace('.docx', '_CORRIGE.docx');
      saveAs(blob, fixedFileName);

      console.log('✅ Template corrigé téléchargé:', fixedFileName);

      // Afficher un message de succès
      setValidationResult({
        ...validationResult,
        fixed: true
      });

    } catch (error) {
      console.error('❌ Erreur correction:', error);
      alert('Erreur lors de la correction: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }} className="p-6">
      <Card>
        <Title level={2}>🔍 Validateur de Template Word</Title>

        <Alert
          message="Vérifiez si vos templates Word ont des variables cassées"
          description="Uploadez votre template Contrat.docx pour détecter les problèmes de formatage avant de l'utiliser."
          type="info"
          showIcon
          className="mb-6"
        />

        <Upload.Dragger
          accept=".docx"
          beforeUpload={handleUpload}
          showUploadList={false}
          disabled={loading}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Cliquez ou glissez votre template .docx ici</p>
          <p className="ant-upload-hint">
            Sélectionnez Contrat.docx, Confirmation.docx, etc.
          </p>
        </Upload.Dragger>

        {loading && (
          <Alert
            message="Analyse en cours..."
            type="info"
            showIcon
            className="mt-4"
          />
        )}

        {validationResult && !validationResult.error && (
          <div className="mt-6">
            <Divider />

            {/* Info sur le type de document */}
            <Alert
              message={`📄 Document: ${validationResult.fileName}`}
              description={
                <div>
                  <p className="mb-1">
                    <strong>Type détecté:</strong>{' '}
                    {validationResult.fileName.toLowerCase().includes('facturation')
                      ? 'Facturation (24 variables attendues avec invoice_number)'
                      : validationResult.fileName.toLowerCase().includes('confirmation')
                      ? 'Confirmation (23 variables attendues sans invoice_number)'
                      : 'Contrat (23 variables attendues sans invoice_number)'}
                  </p>
                  <p className="mb-0">
                    <strong>Variables trouvées:</strong> {validationResult.foundVariables?.length || 0} / {(validationResult.validVariables?.length || 0) + (validationResult.missingVariables?.length || 0)}
                  </p>
                </div>
              }
              type="info"
              showIcon
              className="mb-4"
            />

            <Alert
              message={validationResult.isValid ? '✅ Template VALIDE' : '❌ Template INVALIDE'}
              description={
                validationResult.isValid
                  ? 'Toutes les variables sont correctement formatées. Ce template peut être uploadé dans Supabase.'
                  : 'Des problèmes ont été détectés dans ce template. Corrigez-les avant de l\'utiliser.'
              }
              type={validationResult.isValid ? 'success' : 'error'}
              showIcon
              icon={validationResult.isValid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              className="mb-4"
            />

            {/* Bouton de correction automatique */}
            {!validationResult.isValid && !validationResult.fixed && (
              <div className="mb-4">
                <Button
                  type="primary"
                  size="large"
                  icon={<ToolOutlined />}
                  onClick={fixTemplate}
                  loading={loading}
                  block
                  style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                  🔧 Corriger automatiquement et télécharger
                </Button>
                <Paragraph type="secondary" className="mt-2 mb-0">
                  Cette fonction va nettoyer toutes les variables cassées et ajouter les variables manquantes à la fin du document.
                </Paragraph>
              </div>
            )}

            {/* Message de succès après correction */}
            {validationResult.fixed && (
              <Alert
                message="✅ Template corrigé téléchargé !"
                description={
                  <div>
                    <Paragraph className="mb-2">
                      Le fichier <Text code>{originalFileName.replace('.docx', '_CORRIGE.docx')}</Text> a été téléchargé avec succès.
                    </Paragraph>
                    <Paragraph className="mb-2">
                      <Text strong>Prochaines étapes :</Text>
                    </Paragraph>
                    <ol style={{ paddingLeft: '20px', marginBottom: 0 }}>
                      <li>Ouvrez le fichier téléchargé dans Microsoft Word</li>
                      <li>Vérifiez que toutes les variables sont correctement placées</li>
                      <li>Ajustez la mise en forme si nécessaire</li>
                      <li>Uploadez le fichier dans Supabase Storage {">"} templates</li>
                    </ol>
                  </div>
                }
                type="success"
                showIcon
                icon={<DownloadOutlined />}
                className="mb-4"
              />
            )}

            {validationResult.hasBrokenVariables && (
              <Alert
                message={`⚠️ ${validationResult.brokenCount} variable(s) cassée(s) détectée(s)`}
                description="Des balises XML Word sont présentes DANS les variables {{}}. Cela arrive quand vous appliquez du formatage pendant que vous tapez la variable."
                type="error"
                showIcon
                icon={<WarningOutlined />}
                className="mb-4"
              />
            )}

            {validationResult.unclosedBrackets > 0 && (
              <Alert
                message={`⚠️ ${validationResult.unclosedBrackets} accolade(s) non fermée(s)`}
                description="Certaines variables ont {{ sans }} correspondant."
                type="warning"
                showIcon
                className="mb-4"
              />
            )}

            <Card size="small" className="mb-4" title={`✅ Variables valides trouvées (${validationResult.validVariables?.length || 0}/${validationResult.expectedVariables?.length || 0})`}>
              {(validationResult.validVariables?.length || 0) > 0 ? (
                <div>
                  {validationResult.validVariables.map(v => (
                    <Tag color="green" key={v} className="mb-2">
                      {`{{${v}}}`}
                    </Tag>
                  ))}
                </div>
              ) : (
                <Text type="secondary">Aucune variable valide trouvée</Text>
              )}
            </Card>

            {(validationResult.missingVariables?.length || 0) > 0 && (
              <Card size="small" className="mb-4" title={`❌ Variables manquantes (${validationResult.missingVariables?.length || 0})`} style={{ borderColor: '#ff4d4f' }}>
                <Paragraph>
                  Ces variables sont attendues mais absentes du template :
                </Paragraph>
                {validationResult.missingVariables.map(v => (
                  <Tag color="red" key={v} className="mb-2">
                    {`{{${v}}}`}
                  </Tag>
                ))}
              </Card>
            )}

            {(validationResult.extraVariables?.length || 0) > 0 && (
              <Card size="small" className="mb-4" title={`⚠️ Variables inconnues (${validationResult.extraVariables?.length || 0})`} style={{ borderColor: '#faad14' }}>
                <Paragraph>
                  Ces variables sont dans le template mais ne seront pas remplies :
                </Paragraph>
                {validationResult.extraVariables.map(v => (
                  <Tag color="orange" key={v} className="mb-2">
                    {`{{${v}}}`}
                  </Tag>
                ))}
              </Card>
            )}

            <Divider />

            <Title level={4}>🔧 Comment corriger :</Title>
            <List
              size="small"
              dataSource={[
                {
                  title: 'Variables cassées par le formatage',
                  description: 'Supprimez la variable et retapez-la EN UNE SEULE FOIS. Appliquez le formatage APRÈS.',
                  show: validationResult.hasBrokenVariables
                },
                {
                  title: 'Variables manquantes',
                  description: 'Ajoutez les variables manquantes listées ci-dessus.',
                  show: (validationResult.missingVariables?.length || 0) > 0
                },
                {
                  title: 'Variables inconnues',
                  description: 'Vérifiez l\'orthographe ou supprimez-les si inutilisées.',
                  show: (validationResult.extraVariables?.length || 0) > 0
                }
              ].filter(item => item.show)}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={<Text strong>{item.title}</Text>}
                    description={item.description}
                  />
                </List.Item>
              )}
            />

            {!validationResult.isValid && (
              <Alert
                message="💡 Astuce rapide"
                description="Utilisez l'outil 'Créateur de Template Propre' (create_clean_template.html) pour générer un template sans erreurs automatiquement."
                type="info"
                showIcon
                className="mt-4"
              />
            )}
          </div>
        )}

        {validationResult?.error && (
          <Alert
            message="Erreur"
            description={validationResult.error}
            type="error"
            showIcon
            className="mt-4"
          />
        )}
      </Card>
    </div>
  );
}

export default TemplateValidator;
